/**
 * Motion — ce qui bouge, et de combien (Brand core V16, chap. 13).
 *
 * Trois durées et une courbe, partagées par tout composant animé : `micro` (un galet, une
 * sortie), `standard` (une entrée de bandeau), `entree` (l'entrée d'une page). La courbe est
 * celle du livre V16, une décélération franche — « jamais de rebond, une chose à la fois ». Un composant lit
 * `theme.motion` via `useUnistyles()` et passe la durée à zéro quand `useReduceMotion()` le dit.
 */
export const motion = {
    micro: 150,
    standard: 250,
    entree: 400,
    /** `cubic-bezier(.2, .8, .2, 1)` (V16) — à passer à `Easing.bezier(...motion.easing)`. */
    easing: [0.2, 0.8, 0.2, 1],
} as const

export type MotionKey = "micro" | "standard" | "entree"

/**
 * La respiration de la pause zen (`<CorrectionPause kind="breathing">`) : la durée d'un temps,
 * inspire ou expire, celle de l'ancienne pause (`PendingAction.BreathingCard`, 2,2 s).
 *
 * Un rythme de CONTENU, pas une durée de marque : on respire à cette cadence, on ne regarde pas une
 * interface bouger. Il reste donc hors du thème et du kit de marque (`dev/brand/generer.js` ne lit
 * que `motion`) et se lit ici directement, comme les nombres de `effects.ts`. La courbe, elle, est
 * celle de la marque.
 */
export const breath = {
    half: 2200,
} as const

/**
 * Le battement de la pastille du direct (`<LiveDot>`) : le temps que son halo parte du point et s'efface.
 * Un rythme de CONTENU, comme `breath` : il dit « c'est en ce moment », il ne mesure pas un geste. Hors du
 * thème et du kit de marque, pour la même raison.
 */
export const livePulse = {
    period: 1600,
} as const

/**
 * Le clignotement d'une vraie alerte (`<Blink>`) : la chip d'un élève du live sorti de l'appli ou silencieux
 * depuis 10 min. Le rythme de l'ancienne pastille (`LiveParticipantIndicator`, 700 ms vers 40 %, 700 ms
 * retour), que les tuteurs reconnaissent. Un rythme de CONTENU, comme `livePulse`.
 */
export const alertBlink = {
    half: 700,
    dim: 0.4,
} as const

/**
 * L'ondulation de « il parle » (`<VoiceBars>`) : le temps qu'une barre monte et redescende. Un rythme de CONTENU,
 * comme `livePulse` : il dit « en ce moment il parle », il ne mesure ni la voix ni un geste.
 */
export const voiceWave = {
    period: 900,
} as const
